import type { AbilityService } from '@efdigital/ll-addon-auth';
import { EditRecord } from '@efdigital/ll-admin-v2/components/edit-record.gts';
import { type EditRecordSignature } from '@efdigital/ll-admin-v2/components/edit-record-base.gts';
import {
  buildCreateConsumingPlatform,
  buildUpdateConsumingPlatform,
} from '@efdigital/ll-admin-v2/data/builders/lti/lti-consuming-platforms';
import { deepRollback } from '@efdigital/ll-admin-v2/data/deep-rollback';
import type { LtiConsumingPlatform } from '@efdigital/ll-admin-v2/data/types/lti';
import { platformPhaseStatus } from '@efdigital/ll-admin-v2/utils/lti/setup-status';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';

export class EditLtiConsumingPlatform extends Component<
  EditRecordSignature<
    LtiConsumingPlatform,
    {
      initialState: 'readonly' | 'edit';
    }
  >
> {
  @service declare store: Store;
  @service declare ability: AbilityService;

  get status() {
    return platformPhaseStatus(this.args.item);
  }

  setName = (event: Event) => {
    this.args.item.name = (event.target as HTMLInputElement).value;
  };

  setDescription = (event: Event) => {
    this.args.item.description = (event.target as HTMLInputElement).value;
  };

  setIss = (event: Event) => {
    const iss = (event.target as HTMLInputElement).value;
    this.args.item.platformData = {
      ...this.args.item.platformData,
      iss,
    };
  };

  savePlatform = () => {
    const platform = this.args.item;

    if (platform.isNew) {
      return this.store.request(buildCreateConsumingPlatform(platform));
    }

    return this.store.request(buildUpdateConsumingPlatform(platform));
  };

  revertPlatform = () => {
    deepRollback(this.args.item, this.store);
  };

  get canEditPlatform() {
    return this.ability.canEdit('lti-integrations');
  }

  get showIssuerWarning() {
    return this.args.context.isEditing || this.status !== 'complete';
  }

  <template>
    <EditRecord
      @editLabel="Edit platform details"
      @item={{@item}}
      @context={{@context}}
      @canEdit={{this.canEditPlatform}}
      @maxDepth={{0}}
      @save={{this.savePlatform}}
      @revert={{this.revertPlatform}}
      @initialState={{@initialState}}
      @buttonSize="btn-md"
      ...attributes
    >
      <:form as |uniqueId isReadOnly|>
        <fieldset disabled={{isReadOnly}} class="mb-0">
          <div class="mb-3">
            <label
              class="form-label required"
              for="{{uniqueId}}-name"
            >Name</label>
            <input
              id="{{uniqueId}}-name"
              type="text"
              class="form-control"
              required
              value={{@item.name}}
              data-test-lti-platform-name
              {{on "input" this.setName}}
            />
          </div>
          <div class="mb-3">
            <label
              class="form-label required"
              for="{{uniqueId}}-description"
            >Description</label>
            <input
              id="{{uniqueId}}-description"
              type="text"
              class="form-control"
              required
              value={{@item.description}}
              data-test-lti-platform-description
              {{on "input" this.setDescription}}
            />
          </div>

          {{#if this.showIssuerWarning}}
            <hr class="my-4" />
            <div class="alert alert-warning">
              Ask the customer for their platform issuer value (<code
              >iss</code>).<br />You should use
              <code>TODO</code>
              as a placeholder for the issuer until the customer provides it.
            </div>
          {{/if}}
          <div class="mb-3">
            <label class="form-label required" for="{{uniqueId}}-iss">Issuer
              (iss)</label>
            <input
              id="{{uniqueId}}-iss"
              type="text"
              class="form-control font-monospace"
              required
              value={{@item.platformData.iss}}
              data-test-lti-platform-iss
              {{on "input" this.setIss}}
            />
          </div>
        </fieldset>
      </:form>
    </EditRecord>
  </template>
}

export default EditLtiConsumingPlatform;
